import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Button,
    Container,
    FormControl,
    FormGroup,
    Grid,
    InputLabel,
    makeStyles,
    NativeSelect,
    TextField
} from "@material-ui/core";
import {AppContext, AuthContext} from '../../context';
import {useDataAccess, useForm} from '../custom_hooks';
import {parseError} from "../../utils/hydraParser";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    option: {
        color: theme.palette.text.customPrimary,
    },
}));

const Create = props => {
    const { t } = useTranslation();
    const { authState } = useContext(AuthContext);
    const {
        appState,
        fetchInitAppState,
        fetchSuccessAppState,
        fetchErrorAppState,
        addRefreshResourceAppState,
        forceRefreshResourceAppState,
        showMessageAppState,
        setPageInfos
    } = useContext(AppContext);
    const classes = useStyles();
    const [organizations, setOrganizations] = useState([]);
    const {fetch} = useDataAccess();
    const handleCreate = () => {
        let role, type;
        switch(props.match.params.type) {
            case 'suppliers':
                role = ['ROLE_SUPPLIER'];
                type = 'supplier';
                break;
            case 'resellers':
                role = ['ROLE_RESELLER'];
                type = 'reseller';
                break;
            case 'customers':
                role = ['ROLE_CUSTOMER'];
                type = 'customer';
                break;
            default:
                role = (authState && authState.user && authState.user.roles && authState.user.roles.includes('ROLE_SUPERADMIN')) ? [inputs.roles] : ['ROLE_CUSTOMER'];
                type = 'customer';
        }
        fetchInitAppState();
        fetch(
            'users',
            {
                method: 'POST',
                body: {
                    email: inputs.email,
                    firstName: inputs.firstName,
                    lastName: inputs.lastName,
                    roles: role,
                    type: type,
                    organization: props.match.params.id ? `/api/${props.match.params.type}/${props.match.params.id}` : `/api/${props.match.params.type}/${inputs.organization}`
                }
            }
        )
            .then(() => {
                fetchSuccessAppState();
                if(
                    authState &&
                    authState.user &&
                    authState.user.roles &&
                    authState.user.roles.includes('ROLE_SUPERADMIN')
                ) {
                    forceRefreshResourceAppState('users');
                }
                showMessageAppState('success', t('user_created'));
                props.history.goBack();
            })
            .catch(error => {
                fetchErrorAppState(parseError(error));
            });
    };
    const {inputs, handleInputChange, handleSubmit} = useForm(handleCreate);

    useEffect(() => {
        setPageInfos('new_user', false);
        switch(props.match.params.type) {
            case 'suppliers':
                if(
                    authState.user &&
                    !authState.user.roles.includes('ROLE_SUPERADMIN') &&
                    !authState.user.roles.includes('ROLE_ADMIN')
                ) {
                   props.history.goBack();
                }
                break;
            case 'resellers':
                if(
                    authState.user &&
                    !authState.user.roles.includes('ROLE_SUPERADMIN') &&
                    !authState.user.roles.includes('ROLE_ADMIN') &&
                    !authState.user.roles.includes('ROLE_SUPPLIER')
                ) {
                    props.history.goBack();
                }
                break;
            default:
        }
        if(
            authState &&
            authState.user &&
            authState.user.roles.includes('ROLE_SUPERADMIN')
        ) {
            setOrganizationsFromCache();
            addRefreshResourceAppState('organizations');
        }
        // eslint-disable-next-line
    }, []);

    const setOrganizationsFromCache = () => {
        if(
            appState &&
            appState.cachedData &&
            appState.cachedData.organizations &&
            authState &&
            authState.user &&
            authState.user.roles.includes('ROLE_SUPERADMIN')
        ) {
            setOrganizations(
                Object.keys(appState.cachedData.organizations)
                    .filter(organizationId => appState.cachedData.organizations[organizationId]['@type'].toLowerCase()  === props.match.params.type.slice(0, -1))
                    .map(organizationId => appState.cachedData.organizations[organizationId])
            );
        }

    };

    return (
        <Container>
            <Grid
                container
                direction="row"
                justify="space-evenly"
                alignItems="center"
            >
                {
                    appState.isError &&
                    appState.error &&
                    (
                        <Grid item xs={12} className="text-center">
                            <div className="alert alert-danger" role="alert">
                                <span className="fa fa-exclamation-triangle" aria-hidden="true"/>
                                {
                                    appState.error.errors &&
                                    <div>{appState.error.errors._error}</div>
                                }
                                {
                                    !appState.error.errors &&
                                    <div>{appState.error}</div>
                                }
                            </div>
                        </Grid>
                    )
                }
                <form onSubmit={handleSubmit} className="container">
                    <FormGroup>
                        <FormControl className={classes.formControl}>
                            <TextField
                                margin="normal"
                                error={appState.isError}
                                id="email"
                                name="email"
                                label={t('email_address')}
                                type="text"
                                onChange={handleInputChange}
                                value={inputs.email ? inputs.email : ''}
                                disabled={
                                    appState.isLoading &&
                                    appState.isRefreshing
                                }
                                required
                            />
                        </FormControl>
                        <FormControl className={classes.formControl}>
                            <TextField
                                margin="normal"
                                error={appState.isError}
                                id="firstName"
                                name="firstName"
                                label={t('first_name')}
                                type="text"
                                onChange={handleInputChange}
                                value={inputs.firstName ? inputs.firstName : ''}
                                disabled={
                                    appState.isLoading &&
                                    appState.isRefreshing
                                }
                                required
                            />
                        </FormControl>
                        <FormControl className={classes.formControl}>
                            <TextField
                                margin="normal"
                                error={appState.isError}
                                id="lastName"
                                name="lastName"
                                label={t('last_name')}
                                type="text"
                                onChange={handleInputChange}
                                value={inputs.lastName ? inputs.lastName : ''}
                                disabled={
                                    appState.isLoading &&
                                    appState.isRefreshing
                                }
                                required
                            />
                        </FormControl>
                        {
                            props.match.params.type === '' &&
                            authState.user &&
                            authState.user.roles.includes('ROLE_SUPERADMIN') &&
                            <FormControl className={classes.formControl}>
                                <InputLabel htmlFor="role">
                                    {t('role')}
                                </InputLabel>
                                <NativeSelect
                                    required
                                    disabled={
                                        appState.isLoading &&
                                        appState.isRefreshing
                                    }
                                    value={inputs.roles || ''}
                                    onChange={handleInputChange}
                                    inputProps={{
                                        name: 'role',
                                        id: 'role',
                                    }}
                                >
                                    <option value='' />
                                    <option value='ROLE_CUSTOMER' className={classes.option}>{t('customer')}</option>
                                    <option value='ROLE_RESELLER' className={classes.option}>{t('reseller')}</option>
                                    <option value='ROLE_SUPPLIER' className={classes.option}>{t('supplier')}</option>
                                    <option value='ROLE_ADMIN' className={classes.option}>{t('ROLE_ADMIN')}</option>
                                    <option value='ROLE_SUPERADMIN' className={classes.option}>{t('ROLE_SUPERADMIN')}</option>
                                </NativeSelect>
                            </FormControl>
                        }
                        {
                            !props.match.params.id &&
                            authState.user &&
                            authState.user.roles.includes('ROLE_SUPERADMIN') &&
                            <FormControl className={classes.formControl}>
                                <InputLabel htmlFor="organization">
                                    {t('organization')}
                                </InputLabel>
                                <NativeSelect
                                    required
                                    disabled={
                                        appState.isLoading &&
                                        appState.isRefreshing
                                    }
                                    value={inputs.organization || ''}
                                    onChange={handleInputChange}
                                    inputProps={{
                                        name: 'organization',
                                        id: 'organization',
                                    }}
                                >
                                    <option value='' />
                                    {
                                        organizations &&
                                        organizations.length > 0 &&
                                        organizations.map(organization => {
                                            return <option
                                                value={organization.id}
                                                key={organization.id}
                                                className={classes.option}>
                                                    {organization.name}
                                                </option>
                                        })
                                    }
                                </NativeSelect>
                            </FormControl>
                        }
                        <FormControl className={classes.formControl}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={
                                    appState.isLoading &&
                                    appState.isRefreshing
                                }
                            >
                                {t('create')}
                            </Button>
                        </FormControl>
                    </FormGroup>
                </form>
            </Grid>
        </Container>
    )
};

export default Create;